import { api } from "@/lib/apiClient";
import type { PrintJobStatus } from "@/lib/printJob";

export const COVER_PRINT_LOG_CHANGED_EVENT = "cover_print_logged";

export type CoverPrintKind = "project" | "montage" | "werkstatt" | "aufmass";

export const COVER_PRINT_KIND_LABELS: Record<CoverPrintKind, string> = {
  project: "Projekt-Deckblatt",
  montage: "Montage-Deckblatt",
  werkstatt: "Werkstatt-Deckblatt",
  aufmass: "Aufmaß-Deckblatt",
};

export interface CoverPrintLogEntry {
  id: number;
  auftrag_id: number;
  cover_kind: CoverPrintKind | string;
  cover_kind_label?: string;
  location_id?: number | null;
  location_name?: string | null;
  printer_name?: string | null;
  copies?: number | null;
  created_by_login?: string | null;
  created_by_display?: string | null;
  created_at: string;
}

export function coverPrintKindLabel(kind: string | null | undefined): string {
  const key = String(kind || "project").toLowerCase() as CoverPrintKind;
  return COVER_PRINT_KIND_LABELS[key] || "Deckblatt";
}

export function coverPrintActor(entry: CoverPrintLogEntry): string {
  return String(entry.created_by_display || entry.created_by_login || "").trim() || "Unbekannt";
}

export function coverPrintPlace(entry: CoverPrintLogEntry): string {
  return String(entry.location_name || entry.printer_name || "").trim() || "unbekanntem Standort";
}

export function latestCoverPrint(
  entries: CoverPrintLogEntry[] | null | undefined,
  kind?: CoverPrintKind,
): CoverPrintLogEntry | null {
  const list = Array.isArray(entries) ? entries : [];
  if (kind) {
    return list.find((entry) => String(entry.cover_kind || "").toLowerCase() === kind) ?? null;
  }
  return list[0] ?? null;
}

export function notifyCoverPrintLogged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(COVER_PRINT_LOG_CHANGED_EVENT));
}

export function getCoverPrintLog(orderId: number): Promise<CoverPrintLogEntry[]> {
  return api.get<CoverPrintLogEntry[]>(
    `/auftraege/${encodeURIComponent(String(orderId))}/cover/print-log`,
    { cache: "no-store" },
  );
}

export async function logCoverPrint(
  orderId: number,
  body: {
    cover_kind?: CoverPrintKind;
    location_id?: number | null;
    location_name?: string | null;
    printer_name?: string | null;
    copies?: number;
  },
  job?: PrintJobStatus | null,
): Promise<CoverPrintLogEntry | null> {
  try {
    const entry = await api.post<CoverPrintLogEntry>(
      `/auftraege/${encodeURIComponent(String(orderId))}/cover/print-log`,
      {
        cover_kind: body.cover_kind || "project",
        location_id: body.location_id ?? null,
        location_name: body.location_name ?? null,
        printer_name:
          body.printer_name ||
          job?.printer_name ||
          job?.printer ||
          job?.result?.printer ||
          null,
        copies: Math.max(1, Number(body.copies) || 1),
      },
    );
    notifyCoverPrintLogged();
    return entry;
  } catch (err) {
    console.warn("[coverPrintLog] Speichern fehlgeschlagen", err);
    return null;
  }
}
